import React from 'react';
// Legacy stylesheet: still imported for its global rules (e.g. `p { margin: 0 }`)
// that other screens rely on. The footer itself is styled by footer-hd.css.
import './style.css';
import './footer-hd.css';

// ---------------------------------------------------------------------------
// HootDex footer - same blocks as hootdex.com:
//   1. Top footer: brand quadrant (logo + quick market links) beside the
//      five-column site map.
//   2. Bottom bar: carbon-neutral badge, copyright and legal links.
//
// Same footer as the hootdex.net listing pages (copied from hootdex-frontend
// so the two sites match). On the exchange, the listing links (`to`) point
// at hootdex.net; everything opens in a new tab.
// ---------------------------------------------------------------------------

const QUICK_LINKS = [
  [
    { label: 'SynthCryptos', to: '/' },
    { label: 'Hybrid', to: '/hybrid' },
    { label: 'XMG Tokens', to: '/xmgTokens' }
  ],
  [
    { label: 'DCN Tokens', to: '/dcn' },
    { label: 'DBT Tokens', to: '/dbt' },
    { label: 'Venture Tokens', to: '/VentureToken' }
  ]
];

const SITEMAP = [
  {
    title: 'Getting Started',
    links: [
      { label: 'Tokens', href: 'https://www.hootdex.com/tokens/' },
      { label: 'Token List', href: 'https://education.hootdex.com/docs-category/all-listings/' },
      { label: 'Fair Market', href: 'https://www.hootdex.com/what-is-the-fair-market-price-wtfm/' },
      { label: 'MVault', href: 'https://www.hootdex.com/the-mvault/' },
      { label: 'Features', href: 'https://www.hootdex.com/innovative-market-features/' }
    ]
  },
  {
    title: 'Asset Classes',
    links: [
      { label: 'SynthCryptos', href: 'https://www.hootdex.com/tokens/synthcrypto/' },
      { label: 'Hybrid Tokens', to: '/hybrid' },
      { label: 'XMG Tokens', href: 'https://www.hootdex.com/tokens/xmg-token-series/' },
      { label: 'EquiTrack', href: 'https://www.hootdex.com/tokens/equitrack-tokens/' },
      { label: 'Perpetuals', href: 'https://www.hootdex.com/hootdex-perpetual-markets/' },
      { label: 'Nexus Tokens', href: 'https://www.hootdex.com/tokens/nexus-tokens' }
    ]
  },
  {
    title: 'Asset Classes & Treasury',
    links: [
      { label: 'Digital Credit Note Tokens', href: 'https://www.hootdex.com/tokens/digital-credit-note-tokens' },
      { label: 'DBTs', href: 'https://www.hootdex.com/tokens/digital-basket-token-dbt/' },
      { label: 'Venture Tokens', href: 'https://www.hootdex.com/tokens/venture-tokens/' },
      { label: 'Multi-Asset DAT', href: 'https://www.hootdex.com/multi-asset-digital-asset-treasury/' }
    ]
  },
  {
    title: 'Market Access',
    links: [
      { label: 'Institutions', href: 'https://www.hootdex.com/institutional-digital-asset-traders/' },
      { label: 'P2P Traders', href: 'https://www.hootdex.com/peer-to-peer-swapping/' },
      { label: 'Blind OTC Desk', href: 'https://www.hootdex.com/hootdex-blind-otc-desk/' }
    ]
  },
  {
    title: 'HootDex Hub',
    links: [
      // "/news" on hootdex.com is its own news page; this app has /News too.
      { label: 'News', href: 'https://www.hootdex.com/news' },
      { label: 'Education', href: 'https://education.hootdex.com/' },
      { label: 'FAQ', href: 'https://education.hootdex.com/docs-category/faq-answered/' },
      { label: 'Support', href: 'https://education.hootdex.com/docs/getting-started-metamask-extension/' },
      { label: 'Maintenance', href: 'https://mchathive.com/profile/hootdex' }
    ]
  }
];

const LEGAL = [
  { label: 'media at hootdex.com', href: 'mailto:media@hootdex.com', lower: true },
  { label: 'Privacy Policy', href: 'https://www.hootdex.com/privacy/' },
  { label: 'Risk Disclosure', href: 'https://www.hootdex.com/risk-disclosure/' },
  { label: 'Legal Disclaimer', href: 'https://www.hootdex.com/legal' },
  { label: 'TOS', href: 'https://www.hootdex.com/tos/' },
  { label: 'Neutrality', href: 'https://www.hootdex.com/neutral-disclosure/' },
  { label: 'AML/KYC', href: 'https://www.hootdex.com/aml-and-kyc-compliance/' }
];

function FooterLink({ link, className }) {
  const href = link.to ? `https://www.hootdex.net${link.to}` : link.href;
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {link.label}
    </a>
  );
}

export default function Footer() {
  return (
    <div className="hd-footer-root">
      {/* ================= TOP FOOTER ================= */}
      <div className="hd-top-footer">
        <div className="hd-top-footer-inner">
          {/* Left panel: brand + quick market links */}
          <div className="hd-mini-nav-quadrant">
            <div className="hd-mini-nav-media-frame">
              <img
                src="https://www.hootdex.com/media/2025/03/HootDex_circle.png"
                alt="HootDex Ecosystem Identity"
                className="hd-mini-nav-asset"
              />
            </div>
            <div className="hd-mini-nav-grid-split">
              {QUICK_LINKS.map((col, i) => (
                <div className="hd-mini-nav-col" key={i}>
                  {col.map((l) => (
                    <FooterLink key={l.label} link={l} className="hd-mini-link" />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Right panel: site map */}
          <nav className="hd-sitemap-inner-wrap" aria-label="HootDex site map">
            {SITEMAP.map((col) => (
              <div className="hd-sitemap-column" key={col.title}>
                <h4 className="hd-sitemap-title">{col.title}</h4>
                <div className="hd-sitemap-links-stack">
                  {col.links.map((l) => (
                    <FooterLink key={l.label} link={l} className="hd-sitemap-link" />
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* ================= BOTTOM BAR ================= */}
      <div className="hd-footer-master-wrap">
        <div className="hd-footer-inner-constraint">
          <div className="pn-carbon-badge-container">
            <div className="pn-carbon-badge-content">
              <svg
                className="pn-leaf-graphic"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 3.5 1 9.8a7 7 0 0 1-13.9.2" />
                <path d="M9 22c0-5.1 3.2-7.5 6-10" />
              </svg>
              <span className="pn-carbon-badge-text">
                Built on Pecu Novus, a Carbon Neutral Blockchain Network
              </span>
            </div>
          </div>

          <div className="hd-footer-divider" />

          <footer className="hd-footer-legal-bar">
            <span className="hd-copyright-text">
              &copy; MegaHoot, LLC. All Rights Reserved
            </span>
            <div className="hd-legal-nav-stack">
              {LEGAL.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hd-legal-link${l.lower ? ' hd-text-lowercase' : ''}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
